import { useState, useMemo } from "react";
import { CalendarRange, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { useI18n } from "@/context/I18nContext";
import {
    isoFromDate,
    dateFromIso,
    lastDayOfMonth,
    localeDate,
} from "@/lib/format";
import { OPS } from "@/constants/testIds";

/**
 * Period picker: "All time" + per-month presets + a custom calendar range,
 * bounded to the data coverage window. Emits { start, end, label } where
 * start/end are YYYY-MM-DD (or null for all-time).
 */
export default function PeriodPicker({ meta, value, onChange }) {
    const { t, lang } = useI18n();
    const [open, setOpen] = useState(false);
    const [range, setRange] = useState(
        value?.start ? { from: dateFromIso(value.start), to: dateFromIso(value.end) } : undefined
    );

    const months = useMemo(() => meta?.months || [], [meta?.months]);
    const fromDate = dateFromIso(meta?.coverage_from);
    const toDate = dateFromIso(meta?.coverage_to);

    const label = useMemo(() => {
        if (!value?.start && !value?.end) return t("period_all_time");
        // Recompute the display label in the CURRENT language so it never goes
        // stale after a language toggle. Match a month preset first.
        const am = months.find(
            (m) => value?.start === `${m.value}-01` && (value?.end || "").startsWith(m.value)
        );
        if (am) return lang === "ar" ? am.label_ar : am.label_en;
        if (value?.start && value?.end) return `${localeDate(value.start, lang)} \u2192 ${localeDate(value.end, lang)}`;
        if (value?.label) return value.label;
        return `${value.start} \u2192 ${value.end}`;
    }, [value, t, months, lang]);

    const isAllTime = !value?.start && !value?.end;

    const pickAllTime = () => {
        setRange(undefined);
        onChange({ start: null, end: null, label: t("period_all_time") });
        setOpen(false);
    };

    const pickMonth = (m) => {
        const start = `${m.value}-01`;
        const end = lastDayOfMonth(m.value);
        const clampedEnd = meta?.coverage_to && end > meta.coverage_to ? meta.coverage_to : end;
        setRange({ from: dateFromIso(start), to: dateFromIso(clampedEnd) });
        onChange({ start, end: clampedEnd, label: lang === "ar" ? m.label_ar : m.label_en });
        setOpen(false);
    };

    const applyCustom = () => {
        if (range?.from) {
            const start = isoFromDate(range.from);
            const end = isoFromDate(range.to || range.from);
            onChange({
                start,
                end,
                label: `${localeDate(start, lang)} \u2192 ${localeDate(end, lang)}`,
            });
            setOpen(false);
        }
    };

    const activeMonth = months.find(
        (m) => value?.start === `${m.value}-01`
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    data-testid={OPS.sales.periodPicker}
                    variant="outline"
                    className="gap-2 lg:min-w-[220px] justify-between w-full lg:w-auto"
                >
                    <span className="flex items-center gap-2 truncate">
                        <CalendarRange className="h-4 w-4 text-[color:var(--brand-brown)] shrink-0" />
                        <span className="truncate">{label}</span>
                    </span>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-auto p-0 surface border-[color:var(--border-soft)]"
                dir={lang === "ar" ? "rtl" : "ltr"}
            >
                <div className="flex flex-col sm:flex-row">
                    {/* Presets */}
                    <div className="p-2 sm:w-[170px] sm:border-e border-[color:var(--border-soft)]">
                        <button
                            data-testid={OPS.sales.periodPreset("all")}
                            onClick={pickAllTime}
                            className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors duration-150 hover:bg-[color:rgba(186,217,235,0.35)] ${
                                isAllTime ? "bg-[color:rgba(209,128,47,0.14)] font-semibold text-[color:var(--ink)]" : "text-[color:var(--ink-muted)]"
                            }`}
                        >
                            {t("period_all_time")}
                            {isAllTime ? <Check className="h-4 w-4 text-[color:var(--brand-brown)]" /> : null}
                        </button>
                        <Separator className="my-1" />
                        <div className="max-h-[220px] overflow-y-auto scrollbar-thin">
                            {months.map((m) => {
                                const sel = activeMonth?.value === m.value;
                                // still selectable — the cards inside explain what is missing
                                const empty = m.has_data === false;
                                return (
                                    <button
                                        key={m.value}
                                        data-testid={OPS.sales.periodPreset(m.value)}
                                        onClick={() => pickMonth(m)}
                                        title={empty ? t("period_month_no_data") : undefined}
                                        className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors duration-150 hover:bg-[color:rgba(186,217,235,0.35)] ${
                                            sel ? "bg-[color:rgba(209,128,47,0.14)] font-semibold text-[color:var(--ink)]" : "text-[color:var(--ink-muted)]"
                                        }`}
                                    >
                                        <span className={empty && !sel ? "text-[color:var(--ink-faint)]" : undefined}>
                                            {lang === "ar" ? m.label_ar : m.label_en}
                                        </span>
                                        {sel ? <Check className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                            : empty ? <span className="text-[10px] text-[color:var(--ink-faint)]">{t("period_month_pending")}</span>
                                            : null}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Custom calendar */}
                    <div className="p-2">
                        <p className="px-2 pt-1 pb-2 text-xs font-medium text-[color:var(--ink-muted)]">
                            {t("period_custom")}
                        </p>
                        <Calendar
                            mode="range"
                            dir={lang === "ar" ? "rtl" : "ltr"}
                            selected={range}
                            onSelect={setRange}
                            defaultMonth={toDate}
                            fromDate={fromDate}
                            toDate={toDate}
                            numberOfMonths={1}
                        />
                        <div className="flex items-center justify-end gap-2 px-2 pb-1 pt-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={pickAllTime}
                                className="text-[color:var(--ink-muted)]"
                            >
                                {t("period_reset")}
                            </Button>
                            <Button
                                data-testid={OPS.sales.periodApply}
                                size="sm"
                                disabled={!range?.from}
                                onClick={applyCustom}
                                className="bg-[color:var(--brand-orange)] text-white hover:bg-[color:var(--brand-orange-hover)]"
                            >
                                {t("period_apply")}
                            </Button>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
